import PublishedMenuSection from "./PublishedMenuSection";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import menuData from "../data/menu.json";
import takeawayMenuData from "../data/takeaway-menu.json";

const mainMenu = menuData.menus.find((menu) => menu.id === "main-menu");
const takeawayLunchBoxesSection = takeawayMenuData.menus[0].sections.find(
  (section) => section.id === "lunch-boxes",
);
const lunchTakeawaySection = takeawayLunchBoxesSection
  ? {
      ...takeawayLunchBoxesSection,
      title: "Takeaway Lunch Boxes",
    }
  : null;
const takeawaySections = [
  takeawayLunchBoxesSection,
  ...takeawayMenuData.menus[0].sections.filter(
    (section) => section.id !== "lunch-boxes",
  ),
].filter(Boolean);
const dineInLunchSections = mainMenu.sections.filter((section) =>
  ["lunch-plates", "lunch-combos"].includes(section.id),
);
const lunchSections = [...dineInLunchSections, lunchTakeawaySection].filter(
  Boolean,
);
const dineInSections = mainMenu.sections.filter(
  (section) => !["lunch-plates", "lunch-combos"].includes(section.id),
);

const MENUS = [
  {
    id: "lunch",
    label: "Lunch",
    sections: lunchSections,
  },
  {
    id: "dine-in",
    label: "Dine In",
    sections: dineInSections,
  },
  {
    id: "takeaway",
    label: "Takeaway",
    sections: takeawaySections,
  },
  {
    id: "kids",
    label: "Kids",
    sections: menuData.menus.find((menu) => menu.id === "kids-menu").sections,
  },
];

function formatPrice(price) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: price % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 2,
  }).format(price);
}

function formatFixedPrice(price) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

function renderPrice(item) {
  if (item.price !== undefined && item.price !== null) {
    return item.priceUnit
      ? `${formatPrice(item.price)} ${item.priceUnit}`
      : formatPrice(item.price);
  }

  return null;
}

function renderLunchBoxPrice() {
  return (
    <>
      <span className="menu-item-price-old">{formatFixedPrice(12.5)}</span>
      <span className="menu-item-price-current">{formatFixedPrice(9.5)}</span>
    </>
  );
}

function StaticMenuSection({ takeawayHref, takeawayPhoneNumber }) {
  const [activeMenuId, setActiveMenuId] = useState("dine-in");
  const activeMenu = MENUS.find((menu) => menu.id === activeMenuId);
  const [openSectionId, setOpenSectionId] = useState(null);
  const sectionRefs = useRef({});
  const pendingScrollSectionIdRef = useRef(null);

  useEffect(() => {
    setOpenSectionId(null);
    pendingScrollSectionIdRef.current = null;
  }, [activeMenu]);

  useLayoutEffect(() => {
    if (pendingScrollSectionIdRef.current !== openSectionId) {
      return;
    }

    const sectionElement = sectionRefs.current[openSectionId];
    if (!sectionElement) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      pendingScrollSectionIdRef.current = null;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [openSectionId]);

  return (
    <section className="menu-card" aria-labelledby="menu-heading">
      <div className="menu-header">
        <p className="home-eyebrow">OUR MENUS</p>
        <h2 id="menu-heading">The menu</h2>
        <p className="menu-intro">Choose a menu to see dishes and prices.</p>
        <p className="menu-note">
          Please inform your server of any allergies before ordering.
        </p>
      </div>

      <div className="menu-tabs" role="group" aria-label="Menu types">
        {MENUS.map((menu) => (
          <button
            key={menu.id}
            type="button"
            className={
              menu.id === activeMenuId ? "menu-tab is-active" : "menu-tab"
            }
            aria-pressed={menu.id === activeMenuId}
            onClick={() => setActiveMenuId(menu.id)}
          >
            {menu.label}
          </button>
        ))}
      </div>

      <div className="menu-sections">
        {activeMenu.sections.map((section) => (
          <details
            key={section.id}
            className="menu-group"
            open={section.id === openSectionId}
            ref={(element) => {
              sectionRefs.current[section.id] = element;
            }}
          >
            <summary
              onClick={(event) => {
                event.preventDefault();
                const next = openSectionId === section.id ? null : section.id;
                pendingScrollSectionIdRef.current = next;
                setOpenSectionId(next);
              }}
            >
              <span>{section.title}</span>
              {section.availability ? (
                <span className="menu-group-meta">{section.availability}</span>
              ) : null}
            </summary>
            <div className="menu-group-items">
              {section.items.map((item) => (
                <article key={item.name} className="menu-item">
                  <div className="menu-item-row">
                    <h3>{item.name}</h3>
                    {section.id === "lunch-boxes" ? (
                      <span className="menu-item-price">
                        {renderLunchBoxPrice()}
                      </span>
                    ) : renderPrice(item) ? (
                      <span className="menu-item-price">
                        {renderPrice(item)}
                      </span>
                    ) : null}
                  </div>
                  {item.description ? (
                    <p className="menu-item-description">{item.description}</p>
                  ) : null}
                  {item.variants?.length ? (
                    <ul
                      className="menu-variants"
                      aria-label={`${item.name} options`}
                    >
                      {item.variants.map((variant, index) => (
                        <li key={variant.name} className="menu-variant-chip">
                          <span className="menu-variant-content">
                            <span className="menu-variant-name">
                              {variant.name}
                            </span>
                            <span className="menu-variant-price">
                              {formatPrice(variant.price)}
                            </span>
                          </span>
                          {index < item.variants.length - 1 ? (
                            <span
                              className="menu-variant-divider"
                              aria-hidden="true"
                            >
                              /
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {item.includes?.length ? (
                    <p className="menu-item-includes">
                      Includes: {item.includes.join(", ")}
                    </p>
                  ) : null}
                  {item.notes?.length ? (
                    <p className="menu-item-note">{item.notes.join(" ")}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </details>
        ))}
      </div>

      {activeMenuId === "takeaway" ? (
        <a
          className="takeaway-callout"
          href={takeawayHref}
          aria-label={`Order takeaway in store or call ${takeawayPhoneNumber}`}
        >
          Order takeaway in store or call {takeawayPhoneNumber}
        </a>
      ) : null}
    </section>
  );
}

export default function MenuSection(props) {
  return (
    <PublishedMenuSection
      {...props}
      fallback={<StaticMenuSection {...props} />}
    />
  );
}
